<template>
  <no-ssr>
    <div class="sticky-anchors-menu" :class="{ _visible: visible }">
      <div class="sticky-anchors-menu__container l-outer-width l-inner-padding">
        <div ref="swiper" class="sticky-anchors-menu__swiper swiper">
          <div class="swiper-wrapper">
            <div
              v-for="anchor in anchorsExisting"
              :key="anchor.selector"
              class="sticky-anchors-menu__slide swiper-slide"
            >
              <div
                class="
                  sticky-anchors-menu__item
                  font-semibold
                  u-text-secondary u-clickable
                "
                :class="{ _active: anchor === anchorActive }"
                v-html="anchor.name"
                @click="onItemClick(anchor)"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </no-ssr>
</template>

<script>
import Swiper from "swiper";
import "swiper/swiper.scss";
import documentLoaded from "@/assets/js/utils/documentLoaded";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import noSSR from "vue-no-ssr";

gsap.registerPlugin(ScrollTrigger);

export default {
  components: {
    "no-ssr": noSSR,
  },
  props: {
    anchors: {
      type: Array,
      required: true,
    },
    stickyTrigger: {
      validator: (prop) => typeof prop === "string" || typeof prop === "object",
      default: "body",
    },
  },
  data() {
    return {
      swiperInstance: null,
      stickyScrollTrigger: null,
      visible: false,
      anchorsScrollTriggers: [],
      anchorActive: this.anchors[0],
    };
  },
  computed: {
    anchorsExisting() {
      return this.anchors.filter(
        ({ selector }) => !!document.querySelector(selector)
      );
    },
  },
  mounted() {
    this.initSlider();
    this.initSticky();
    this.initAnchors();
  },
  beforeDestroy() {
    this.destroySlider();
    this.destroySticky();
    this.destroyAnchors();
  },
  watch: {
    stickyTrigger: {
      handler() {
        this.initSticky();
      },
    },
    anchorsExisting: {
      deep: true,
      handler() {
        this.initAnchors();
      },
    },
  },
  methods: {
    initSlider() {
      setTimeout(() => {
        if (this.$refs.swiper === undefined) {
          return;
        }
        this.swiperInstance = new Swiper(this.$refs.swiper, {
          touchReleaseOnEdges: true,
          observer: true,
          slidesPerView: "auto",
          on: {
            init(swiper) {
              documentLoaded(() => {
                setTimeout(() => {
                  swiper.update();
                });
              });
            },
          },
        });
      });
    },
    destroySlider() {
      this.swiperInstance?.destroy();
    },
    initSticky() {
      setTimeout(() => {
        this.destroySticky();

        this.stickyScrollTrigger = ScrollTrigger.create({
          trigger: this.stickyTrigger,
          start: "top top",
          end: "bottom top",
          endTrigger: "html",
          invalidateOnRefresh: true,
          onToggle: ({ isActive }) => {
            this.visible = isActive;
          },
        });
      });
    },
    destroySticky() {
      this.stickyScrollTrigger?.kill();
    },
    initAnchors() {
      setTimeout(() => {
        this.destroyAnchors();

        const { anchorsExisting } = this;
        const getStart = () => `top top+=${this.getOffset() + 1}px`;

        anchorsExisting.forEach((anchor, index) =>
          this.anchorsScrollTriggers.push(
            ScrollTrigger.create({
              trigger: anchor.selector,
              start: getStart,
              ...(anchorsExisting[index + 1]
                ? {
                    end: getStart,
                    endTrigger: anchorsExisting[index + 1].selector,
                  }
                : { end: "bottom top", endTrigger: "html" }),
              invalidateOnRefresh: true,
              onToggle: ({ isActive }) => {
                if (isActive) {
                  this.anchorActive = anchor;
                  this.swiperInstance?.slideTo(index);
                }
              },
            })
          )
        );

        ScrollTrigger.refresh();
      });
    },
    destroyAnchors() {
      this.anchorsScrollTriggers.forEach((trigger) => trigger.kill());
      this.anchorsScrollTriggers = [];
    },
    getOffset() {
      return this.$el.offsetHeight + 50;
    },
    onItemClick({ selector }) {
      ScrollTrigger.refresh();
      this.$scrollTo(selector, 500, {
        offset: -this.getOffset(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$b: ".sticky-anchors-menu";

#{$b} {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 100000;
  background: var.$white;
  box-shadow: 1px 2px 45px rgba(var.$secondary, 0.22);
  padding: 19px 0;
  transition: var.$transition-default;

  &:not(._visible) {
    opacity: 0;
    visibility: hidden;
    transform: translate(0, -100%);
  }

  &__swiper {
    overflow: visible;
  }

  &__slide {
    display: flex;
    width: auto;
    height: auto;
  }

  &__item {
    white-space: nowrap;
    transition: var.$transition-default;

    &._active {
      color: var.$black;
    }

    #{$b}__slide:not(:last-child) & {
      margin-right: 20px;
    }
  }
}
</style>
